// Unpublished Work © 2023-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {connect} from 'react-redux';
import LoadingWrapper from 'Ui/components/common/loading-wrapper';
import MowerStatusBatteryLevel from 'Ui/components/home/electric-mower-status/mower-status-battery-level';
import NoDataMessage from 'Ui/components/panel/no-data-message';
import Panel from 'Ui/components/panel/panel';
import {getMyJDMachineBatteryMeasurements} from 'Services/my-jd-machine-service';
import {fetchEffectData} from 'Utils/react-utils';
import {getElectricMowerThreshold} from 'Utils/threshold-utils';
import {Tooltip} from '@deere/isg.component-library';
import {formatTimeOrDefault} from 'OnEquip/equipment/utils/equipment-detail-formatters';
import {ELECTRIC_MACHINE_VIN_PREFIXES, VIN_PREFIX_LENGTH} from 'Ui/constants/equipment-constants';
import {EXTREME_BATTERY_THRESHOLD} from 'Ui/constants/threshold-constants';
import {isNullOrUndefined} from 'Common/utils/validation-utils';
import DataGridWrapper from '../../common/data-table/data-grid-wrapper';

async function getAggregatedMachineDataWithBatteryMeasurements(tileData, translations) {
    const fleetEquipmentMap = new Map(tileData.equipment.map((equipment) => [equipment.serialNumber, equipment]));
    const electricMachines = tileData.myJdData.filter((machine) => {
        return fleetEquipmentMap.has(machine.serialNumber) &&
            ELECTRIC_MACHINE_VIN_PREFIXES.includes(machine.serialNumber?.slice(0, VIN_PREFIX_LENGTH));
    });
    const machineIdToSerialNumberMap = new Map(electricMachines.map((machine) => [machine.id, machine.serialNumber]));

    const electricMachineAndOrgIds = electricMachines.map((machine) => ({
        machineId: machine.id,
        orgId: machine.organization?.id
    }));
    const aggregatedBatteryMeasurements = await getMyJDMachineBatteryMeasurements(electricMachineAndOrgIds);

    return aggregatedBatteryMeasurements.filter(({value}) => !isNullOrUndefined(value)).map((batteryMeasurement) => {
        const {
            machineId,
            ...machineAggregatedData
        } = batteryMeasurement;

        return {
            ...machineAggregatedData,
            value: Math.round(machineAggregatedData.value),
            name: fleetEquipmentMap.get(machineIdToSerialNumberMap.get(machineId)).equipmentName,
            formattedReportTime: formatTimeOrDefault(machineAggregatedData.lastUpdated),
            state: translations[machineAggregatedData.state] || machineAggregatedData.state
        };
    });
}

function getDataGridColumns(translations) {
    return [
        {
            headerName: translations.NAME,
            field: 'name',
            width: 130,
            renderCell: (params) => {
                const {name} = params.row;

                return (
                    <Tooltip
                        placement='bottom'
                        title={name}
                    >
                        <span className='basic-table-cell-block'>
                            {name}
                        </span>
                    </Tooltip>
                );
            }
        },
        {
            headerName: translations.ONLINK_BATTERY_LEVEL,
            field: 'value',
            width: 90,
            renderCell: (params) => {
                const {
                    value: level, state
                } = params.row;

                const {backgroundClassName} = getElectricMowerThreshold(level);
                const lowBatteryWarning = level < EXTREME_BATTERY_THRESHOLD && state === 'working' ||
                    level < EXTREME_BATTERY_THRESHOLD && state === 'Not Charging';

                return (
                    <MowerStatusBatteryLevel
                        level={level}
                        lowBatteryWarning={lowBatteryWarning}
                        thresholdClassName={backgroundClassName}
                        translations={translations}
                    />
                );
            }
        },
        {
            headerName: translations.ONLINK_MACHINE_STATE,
            field: 'state',
            width: 80
        },
        {
            headerName: translations.LAST_UPDATED,
            field: 'formattedReportTime',
            renderCell: (params) => {
                const {formattedReportTime} = params.row;

                return (
                    <Tooltip
                        placement='bottom'
                        title={formattedReportTime}
                    >
                        <span className='basic-table-cell-block'>{formattedReportTime}</span>
                    </Tooltip>
                );
            }
        }
    ];
}

function ElectricMowerStatus(props) {
    const {
        translations,
        tile: tileData
    } = props;

    const [loading, setLoading] = React.useState(false);
    const [aggregatedMachineData, setAggregatedMachineData] = React.useState([]);

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        setLoading(true);

        const aggregatedMachineData = tileData?.equipment && tileData?.myJdData ?
            await getAggregatedMachineDataWithBatteryMeasurements(tileData, translations) :
            [];

        if (isMounted()) {
            setAggregatedMachineData(aggregatedMachineData);
            setLoading(false);
        }
    }), [tileData]);

    const dataComponent = (
        <DataGridWrapper
            columns={getDataGridColumns(translations)}
            getRowId={(row) => row.name}
            initialState={{
                sorting: {
                    sortModel: [{
                        field: 'value',
                        sort: 'desc'
                    }]
                }
            }}
            noDataAvailableMessage={translations.NO_EQUIPMENT_FOUND}
            rows={aggregatedMachineData}
            slotProps={{
                root: {
                    disableColumnMenu: true
                }
            }}
            translations={translations}
        />
    );

    return (
        <Panel
            title={translations.ONLINK_ELECTRIC_MOWER_STATUS}
            translations={translations}
        >
            <LoadingWrapper
                className='onlink-loading-icon'
                loading={loading}
                size={'50px'}
            >
                {
                    aggregatedMachineData.length ?
                        dataComponent : <NoDataMessage translations={translations}/>
                }
            </LoadingWrapper>
        </Panel>
    );
}

ElectricMowerStatus.propTypes = {
    isgToggles: PropTypes.object,
    tile: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.arrayOf(PropTypes.object)
    ]),
    translations: PropTypes.translations
};

export function mapStateToProps(state) {
    return {
        translations: state.translations
    };
}

export default connect(mapStateToProps)(ElectricMowerStatus);
